import React, { useState, useEffect, useMemo } from "react";
import Routes from "./routes";

import { createTheme, ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";
import { ptBR } from "@mui/material/locale";
import ColorModeContext from "./context/ColorMode/ColorModeContext";

const App = () => {
	const [locale, setLocale] = useState();
	const [mode, setMode] = useState(
		() => localStorage.getItem("themeMode") || "light"
	);

	const colorMode = useMemo(
		() => ({
			mode,
			toggleColorMode: () => {
				setMode((prevMode) => {
					const nextMode = prevMode === "light" ? "dark" : "light";
					localStorage.setItem("themeMode", nextMode);
					return nextMode;
				});
			},
		}),
		[mode]
	);

	useEffect(() => {
		document.documentElement.style.colorScheme = mode;
	}, [mode]);

	const theme = createTheme(
		{
			scrollbarStyles: {
				"&::-webkit-scrollbar": {
					width: "8px",
					height: "8px",
				},
				"&::-webkit-scrollbar-thumb": {
					borderRadius: 8,
					backgroundColor: mode === "light" ? "#c9c9c9" : "#3a4048",
				},
				"&::-webkit-scrollbar-thumb:hover": {
					backgroundColor: mode === "light" ? "#a8a8a8" : "#4a515a",
				},
				"&::-webkit-scrollbar-track": {
					background: "transparent",
				},
				"&::-webkit-scrollbar-button": {
					display: "none",
				},
				"&::-webkit-scrollbar-corner": {
					background: "transparent",
				},
			},
			palette: {
				mode,
				primary: { main: "#a13a3a", dark: "#7a2c2c", light: "#c05f5f" },
				secondary: { main: "#455a64" },
				danger: { main: "#4caf50" },
				background:
					mode === "light"
						? { default: "#f4f6f8", paper: "#ffffff" }
						: { default: "#14171c", paper: "#1c2128" },
				text:
					mode === "light"
						? { primary: "#1f2933", secondary: "#5f6b76" }
						: { primary: "rgba(255,255,255,0.92)", secondary: "rgba(255,255,255,0.6)" },
			},
			shape: {
				borderRadius: 10,
			},
			typography: {
				fontFamily:
					'"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
				fontSize: 13,
				button: {
					textTransform: "none",
					fontWeight: 600,
				},
				h6: { fontWeight: 700 },
				subtitle1: { fontWeight: 600 },
			},
			components: {
				MuiGrid: {
					styleOverrides: {
						root: {
							"&.MuiGrid-container": {
								width: "100%",
							},
							"&.MuiGrid-container > .MuiGrid-root": {
								minWidth: 0,
							},
						},
					},
				},
				MuiIconButton: {
					styleOverrides: {
						sizeLarge: {
							padding: 8,
						},
					},
				},
				MuiButton: {
					defaultProps: {
						disableElevation: true,
					},
					styleOverrides: {
						root: {
							borderRadius: 8,
						},
					},
				},
				MuiPaper: {
					styleOverrides: {
						elevation1: {
							boxShadow: "0 1px 3px rgba(16,24,40,0.08), 0 1px 2px rgba(16,24,40,0.06)",
						},
					},
				},
				MuiDialog: {
					styleOverrides: {
						paper: {
							borderRadius: 14,
							backgroundImage: "none",
							...(mode === "dark" && {
								backgroundColor: "#1c2128",
								border: "1px solid rgba(255,255,255,0.08)",
							}),
						},
						paperFullScreen: {
							borderRadius: 0,
							border: "none",
						},
					},
				},
				MuiChip: {
					styleOverrides: {
						root: {
							fontWeight: 600,
						},
					},
				},
				MuiTab: {
					styleOverrides: {
						root: {
							textTransform: "none",
							fontWeight: 600,
						},
					},
				},
				MuiTableCell: {
					styleOverrides: {
						head: {
							fontWeight: 700,
							color: mode === "light" ? "#5f6b76" : "rgba(255,255,255,0.7)",
							backgroundColor: mode === "light" ? "#fafbfc" : "#20252c",
						},
					},
				},
				MuiTooltip: {
					styleOverrides: {
						tooltip: {
							borderRadius: 8,
							fontSize: 12,
						},
					},
				},
				MuiOutlinedInput: {
					styleOverrides: {
						root: {
							borderRadius: 8,
							"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
								borderColor: mode === "light" ? "#455a64" : "#8fa3ad",
							},
							"&.MuiInputBase-sizeSmall .MuiOutlinedInput-input": {
								paddingTop: 9,
								paddingBottom: 9,
							},
						},
					},
				},
				MuiInputLabel: {
					styleOverrides: {
						root: {
							"&.Mui-focused": {
								color: mode === "light" ? "#455a64" : "#8fa3ad",
							},
						},
					},
				},
			},
		},
		locale || {}
	);

	useEffect(() => {
		const i18nlocale = localStorage.getItem("i18nextLng");
		if (!i18nlocale) return;
		const browserLocale =
			i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5);

		if (browserLocale === "ptBR") {
			setLocale(ptBR);
		}
	}, []);

	return (
		<StyledEngineProvider injectFirst>
			<ColorModeContext.Provider value={colorMode}>
				<ThemeProvider theme={theme}>
					<CssBaseline />
					<GlobalStyles
						styles={{
							"html, body, #root": {
								maxWidth: "100vw",
								overflowX: "hidden",
							},
						}}
					/>
					<Routes />
				</ThemeProvider>
			</ColorModeContext.Provider>
		</StyledEngineProvider>
	);
};

export default App;
